import { Main as Container } from 'Components/Main';
import {
  MenuNotificationBody,
  MenuNotificationContent,
  MenuNotificationIcon,
  MenuNotificationItem,
  MenuNotificationTime,
  MenuNotificationTitle,
} from 'Components/Header/components/MenuNotifications/style';
import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveNotification,
} from 'services/user-notifications-service';
import { useAuthTenant, useAuthUser } from 'store/auth';
import {
  InboxIcon,
  DoneAllIcon,
  ArchiveIcon,
  MarkEmailReadIcon,
} from 'Components/common/Icons';
import { Button, PageHeader } from 'Components/ui';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { TabsBar } from 'Components/common/TabsBar';
import styled from 'styled-components';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import { useTranslation } from 'react-i18next';


const LoadingBox = styled.div`
  padding: 16px;
  text-align: center;
`;

const ErrorBox = styled.div`
  padding: 16px;
  text-align: center;
  color: var(--vo-danger);
`;

const EmptyBox = styled.div`
  padding: 16px;
  text-align: center;
  color: var(--vo-slate);
`;

const ActionBox = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: auto;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const NotificationsContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  border-bottom: 1px solid var(--vo-cloud);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IconButton = styled(Button)`
  min-width: 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;


const NotificationList = ({ notifications, isLoading, isError, page, totalPages, onPageChange, onMarkAsRead, onArchive, showActions = true }) => {
  const { t } = useTranslation(['user', 'common']);
  if (isLoading) {
    return <LoadingBox>{t('common:labels.loading')}</LoadingBox>;
  }

  if (isError) {
    return <ErrorBox>{t('user:notifications.error')}</ErrorBox>;
  }

  if (!notifications || notifications.length === 0) {
    return <EmptyBox>{t('user:notifications.empty')}</EmptyBox>;
  }

  return (
    <>
      {notifications.map((itemNotification, key) => (
        <MenuNotificationItem key={itemNotification._id || key} style={{ position: 'relative' }}>
          <MenuNotificationIcon>
            <InboxIcon />
          </MenuNotificationIcon>
          <MenuNotificationBody>
            <MenuNotificationTitle>{itemNotification.titleNotification}</MenuNotificationTitle>
            <MenuNotificationContent>{itemNotification.bodyNotification}</MenuNotificationContent>
            <MenuNotificationTime>
              {formatDateTimeToDateTimeBR(itemNotification.createdDate, true)}
            </MenuNotificationTime>
          </MenuNotificationBody>
          {showActions && (
            <ActionBox>
              {onMarkAsRead && (
                <IconButton variant="ghost" title={t('user:notifications.markAsRead')} onClick={() => onMarkAsRead(itemNotification._id)}>
                  <MarkEmailReadIcon size={18} />
                </IconButton>
              )}
              {onArchive && (
                <IconButton variant="ghost" title={t('common:tooltips.archive')} onClick={() => onArchive(itemNotification._id)}>
                  <ArchiveIcon size={18} />
                </IconButton>
              )}
            </ActionBox>
          )}
        </MenuNotificationItem>
      ))}
      {totalPages > 1 && (
        <PaginationRow>
          <DefaultPagination
            count={totalPages}
            onChange={(_, selectedPage) => onPageChange(selectedPage)}
            color="primary"
            page={page}
            showFirstButton
            showLastButton
          />
        </PaginationRow>
      )}
    </>
  );
};

export const UserNotifications = () => {
  const { t } = useTranslation(['user', 'common']);
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const queryClient = useQueryClient();
  const [tabPosition, setTabPosition] = useState(0);
  const [pageUnread, setPageUnread] = useState(1);
  const [pageRead, setPageRead] = useState(1);
  const [pageArchived, setPageArchived] = useState(1);

  const {
    data: unreadNotifications,
    isLoading: isLoadingUnread,
    isError: isErrorUnread,
  } = useQuery(['getNotifications', 'unread', user?.userId, selectedTenant?.slug, pageUnread], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageUnread, 15, '', 'unread');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  });

  const {
    data: readNotifications,
    isLoading: isLoadingRead,
    isError: isErrorRead,
  } = useQuery(['getNotifications', 'read', user?.userId, selectedTenant?.slug, pageRead], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageRead, 15, '', 'read');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  }, { enabled: tabPosition === 1 });

  const {
    data: archivedNotifications,
    isLoading: isLoadingArchived,
    isError: isErrorArchived,
  } = useQuery(['getNotifications', 'archived', user?.userId, selectedTenant?.slug, pageArchived], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageArchived, 15, '', 'archived');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  }, { enabled: tabPosition === 2 });

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['getNotifications']);
    queryClient.invalidateQueries(['getTotalCountUserNotification']);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId, user.userId, selectedTenant.slug);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user.userId, selectedTenant.slug);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  const handleArchive = async (notificationId) => {
    try {
      await archiveNotification(notificationId, user.userId, selectedTenant.slug);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao arquivar notificação:', error);
    }
  };

  const handleTabChange = (newValue) => {
    setTabPosition(newValue);
  };

  const tabs = [
    { id: 'tab-news', label: t('user:notifications.tabs.unread') },
    { id: 'tab-readed', label: t('user:notifications.tabs.read') },
    { id: 'tab-archived', label: t('user:notifications.tabs.archived') },
  ];

  return (
    <Container>
      <PageHeader title={t('user:notifications.title')} />
      <NotificationsContainer className="container">
        <HeaderRow>
          <TabsBar tabs={tabs} activeTab={tabPosition} onChange={handleTabChange} />
          {tabPosition === 0 && unreadNotifications?.notifications?.length > 0 && (
            <Button
              variant="secondary"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              {t('user:notifications.markAllAsRead')}
            </Button>
          )}
        </HeaderRow>
        <div role="tabpanel" hidden={tabPosition !== 0} id="tabpanel-news" aria-labelledby="tab-news">
          {tabPosition === 0 && (
            <div style={{ padding: '8px 0px' }}>
              <NotificationList
                notifications={unreadNotifications?.notifications}
                isLoading={isLoadingUnread}
                isError={isErrorUnread}
                page={unreadNotifications?.page || 1}
                totalPages={unreadNotifications?.totalPages || 0}
                onPageChange={setPageUnread}
                onMarkAsRead={handleMarkAsRead}
                onArchive={handleArchive}
              />
            </div>
          )}
        </div>
        <div role="tabpanel" hidden={tabPosition !== 1} id="tabpanel-readed" aria-labelledby="tab-readed">
          {tabPosition === 1 && (
            <div style={{ padding: '8px 0px' }}>
              <NotificationList
                notifications={readNotifications?.notifications}
                isLoading={isLoadingRead}
                isError={isErrorRead}
                page={readNotifications?.page || 1}
                totalPages={readNotifications?.totalPages || 0}
                onPageChange={setPageRead}
                onArchive={handleArchive}
                showActions={true}
                onMarkAsRead={null}
              />
            </div>
          )}
        </div>
        <div role="tabpanel" hidden={tabPosition !== 2} id="tabpanel-archived" aria-labelledby="tab-archived">
          {tabPosition === 2 && (
            <div style={{ padding: '8px 0px' }}>
              <NotificationList
                notifications={archivedNotifications?.notifications}
                isLoading={isLoadingArchived}
                isError={isErrorArchived}
                page={archivedNotifications?.page || 1}
                totalPages={archivedNotifications?.totalPages || 0}
                onPageChange={setPageArchived}
                showActions={false}
              />
            </div>
          )}
        </div>
      </NotificationsContainer>
    </Container>
  );
};
